import type { CallTranscriptMessage, TranscriptSnippet } from "@expert/shared-types";

import { type GaiaWebSocketResponse, gaiaWsEventBus } from "@expert/gaia";
import { useEffect, useEffectEvent } from "react";

import { getRootDispatcher } from "@/analytics";
import { mergeSnippets } from "@/transcript/utils/mergeSnippets";

const TRANSCRIPT_SNIPPET_LOG_INTERVAL = 10000;

export const useTranscriptSnippetListener = (
    setSnippets: (value: React.SetStateAction<TranscriptSnippet[] | undefined>) => void,
    // When provided, snippets whose `body.sessionId` !== `activeCallSid` are ignored; `activeCallSid` is
    // undefined once the call has ended, so all trailing snippets are dropped. Legacy callers pass no scope.
    callScope?: { activeCallSid?: string },
) => {
    const onCallTranscript = useEffectEvent(({ body }: GaiaWebSocketResponse) => {
        const { transcripts, timestamp: gaiaTimestamp, sessionId } = body as CallTranscriptMessage;

        // Ignore snippets that don't belong to the active call (and all snippets once the call has ended).
        if (callScope && sessionId !== callScope.activeCallSid) return;

        if (transcripts) {
            const exwoTimestamp = Date.now();

            transcripts.forEach((t) => {
                const shouldSend = Math.floor(Math.random() * TRANSCRIPT_SNIPPET_LOG_INTERVAL) + 1 === 1;
                if (shouldSend) {
                    void getRootDispatcher().dispatchBusinessEvent("TranscriptSnippetReceived", {
                        gaiaTimestamp,
                        exwoTimestamp: exwoTimestamp,
                        snippetTimestamp: t.timestamp,
                    });
                }
            });
            setSnippets((prevState) => mergeSnippets(prevState, transcripts));
        }
    });

    useEffect(() => gaiaWsEventBus.on("gaia_ws_call-transcript", onCallTranscript), []);
};
